import {
  COSMETICS_CATALOG,
  COSMETIC_SLOTS,
  DEFAULT_COSMETICS,
  type CosmeticSlot,
} from "@neon-purge/shared";
import { el } from "./dom.js";
import { api } from "../net/api.js";
import { CharacterPreview } from "../game/previews.js";

const SLOT_LABELS: Record<CosmeticSlot, string> = {
  bodySkin: "Body",
  headgear: "Headgear",
  knifeSkin: "Knife Skin",
  killEffect: "Kill Effect",
  glowColor: "Glow Color",
  weaponTint: "Weapon Tint",
};

/** Customise screen: live rotating 3D character + cosmetic pickers + skin lab. */
export function cosmeticsScreen(onBack: () => void, onSkinLab?: () => void): HTMLElement {
  const screen = el("div", { class: "screen cos-screen" });
  const status = el("div", { class: "error" });

  const equipped: Record<string, string> = {
    ...DEFAULT_COSMETICS,
    ...(api.account?.cosmetics ?? {}),
  };

  let preview: CharacterPreview | null = null;
  const cleanup = (): void => { preview?.dispose(); preview = null; };
  const applyToPreview = (): void => preview?.setCosmetics({
    bodySkin: equipped.bodySkin,
    glowColor: equipped.glowColor,
    headgear: equipped.headgear,
    knifeSkin: equipped.knifeSkin,
    skinUrl: api.account?.skinUrl,
  });

  const canvas = document.createElement("canvas");
  canvas.className = "char3d";

  const slotRows = COSMETIC_SLOTS.map((slot) => {
    const items = COSMETICS_CATALOG[slot];
    const swatches = el("div", { class: "cos-swatches" });
    const rebuild = (): void => {
      swatches.replaceChildren(...items.map((item) => {
        const sw = el("button", {
          class: `cos-swatch${equipped[slot] === item.id ? " active" : ""}`,
          title: item.name,
          style: item.value ? `--sw:${item.value}` : "--sw:#333a4a",
        });
        sw.textContent = item.value ? "" : "∅";
        sw.onclick = () => { equipped[slot] = item.id; rebuild(); applyToPreview(); };
        return sw;
      }));
    };
    rebuild();
    return el("div", { class: "cos-row" }, el("label", { text: SLOT_LABELS[slot] }), swatches);
  });

  const save = el("button", { class: "primary wide", text: "Save Loadout" });
  save.onclick = async () => {
    save.disabled = true;
    status.textContent = "";
    try {
      const patch = Object.fromEntries(COSMETIC_SLOTS.map((s) => [s, equipped[s]]));
      await api.patchCosmetics(patch);
      status.style.color = "var(--cyan)";
      status.textContent = "saved ✓";
    } catch (err) {
      status.style.color = "var(--danger)";
      status.textContent = (err as Error).message;
    } finally { save.disabled = false; }
  };

  const skinLab = el("button", { class: "magenta wide", text: "🎨 Skin Lab — draw / add a picture" });
  skinLab.onclick = () => { cleanup(); onSkinLab?.(); };


  const back = el("button", { class: "ghost wide", text: "Back" });
  back.onclick = () => { cleanup(); onBack(); };

  const hasSkin = !!api.account?.skinUrl;
  screen.append(
    el("div", { class: "cos-head" },
      el("div", { class: "brand small", text: "NEON PURGE" }),
      el("div", { class: "brand-sub", text: "CUSTOMISE" })),
    el("div", { class: "cos-grid" },
      el("div", { class: "card char-card" },
        el("div", { class: "card-title", text: "YOUR OPERATIVE" }),
        el("div", { class: "char-name", text: (api.account?.username ?? "OPERATIVE").toUpperCase() }),
        el("div", { class: "char-class", text: "◈ WRAITH-CLASS ◈" }),
        el("div", { class: "char3d-wrap" }, canvas, el("div", { class: "map3d-hint", text: "drag to rotate" })),
        el("div", { class: "char3d-note", text: hasSkin ? "✓ custom skin applied" : "no custom skin yet — open the Skin Lab" })),
      el("div", { class: "card" },
        el("div", { class: "card-title", text: "COSMETICS" }),
        el("div", { class: "cos-slots" }, ...slotRows),
        save,
        onSkinLab ? skinLab : el("span", {}),
        status),
    ),
    back,
  );

  requestAnimationFrame(() => {
    if (!screen.isConnected) return;
    preview = new CharacterPreview(canvas, {
      bodySkin: equipped.bodySkin, glowColor: equipped.glowColor,
      headgear: equipped.headgear, knifeSkin: equipped.knifeSkin,
      skinUrl: api.account?.skinUrl,
    });
    preview.start();
  });

  return screen;
}
